import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter } from 'lucide-react';
import apiService from '../services/nextlevelgames-api';
import { useStore } from '../context/StoreContext';
import ProductCard from '../components/ProductCard';
import FilterSearchInput from '../components/FilterSearchInput';
import SearchBox from '../components/SearchBox';
import { buildCategoryIndex, applyFacets } from '../utils/searchFacets';

// Facets are built from the full result set, so pull every page (capped).
const PAGE_SIZE = 100;
const MAX_RESULTS = 500;
const VISIBLE_OPTIONS = 8;

const FacetList = ({ options, selected, onSelect, expanded, onToggleExpanded, searchPlaceholder }) => {
  const [filterText, setFilterText] = useState('');
  const needle = filterText.trim().toLowerCase();
  const matches = needle ? options.filter((o) => o.value === selected || o.value.toLowerCase().includes(needle)) : options;
  // While typing, show every match; otherwise collapse long lists
  const shown = needle || expanded || !onToggleExpanded ? matches : matches.filter((o, i) => i < VISIBLE_OPTIONS || o.value === selected);
  return (
    <div className="space-y-1">
      {searchPlaceholder && options.length > VISIBLE_OPTIONS && (
        <FilterSearchInput value={filterText} onChange={setFilterText} placeholder={searchPlaceholder} />
      )}
      {needle && matches.length === 0 && (
        <p className="px-2 text-sm text-gray-500">No matches for "{filterText.trim()}"</p>
      )}
      {shown.map(({ value, count }) => (
        <button
          key={value}
          type="button"
          onClick={() => onSelect(value)}
          aria-pressed={selected === value}
          className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm text-left transition-colors ${
            selected === value ? 'bg-primary-100 text-primary-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <span className="truncate">{value}</span>
          <span className="text-xs text-gray-400 flex-shrink-0">{count}</span>
        </button>
      ))}
      {!needle && onToggleExpanded && options.length > VISIBLE_OPTIONS && (
        <button type="button" onClick={onToggleExpanded} className="text-xs text-primary-600 hover:underline px-2 pt-1">
          {expanded ? 'Show fewer' : `Show all ${options.length}`}
        </button>
      )}
    </div>
  );
};

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const { locations, selectedLocation, setSelectedLocation } = useStore();

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [categoryIndex, setCategoryIndex] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [dimFilters, setDimFilters] = useState({});
  const [expandedDims, setExpandedDims] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState(null);
  const [skuMatch, setSkuMatch] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [storeOnly, setStoreOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 1024);
  const limit = 25;

  const SORT_OPTIONS = [
    { value: null,         label: 'Relevance' },
    { value: 'name',       label: 'Name: A–Z' },
    { value: 'name_desc',  label: 'Name: Z–A' },
    { value: 'price_asc',  label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
  ];

  const fetchResults = useCallback(async (q, off = 0, s = sort, onlineOnlyFlag = onlineOnly, storeOnlyFlag = storeOnly) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const locationId = selectedLocation?.id || null;
      const first = await apiService.searchCatalog(q.trim(), PAGE_SIZE, 0, locationId, s, onlineOnlyFlag, storeOnlyFlag);
      const count = first.total || 0;
      const rest = [];
      for (let o = PAGE_SIZE; o < Math.min(count, MAX_RESULTS); o += PAGE_SIZE) {
        rest.push(apiService.searchCatalog(q.trim(), PAGE_SIZE, o, locationId, s, onlineOnlyFlag, storeOnlyFlag));
      }
      const pages = await Promise.all(rest);
      setResults([...(first.items || []), ...pages.flatMap((pg) => pg.items || [])]);
      setTotal(count);
      setOffset(off);
      setSkuMatch(first.sku_match === true);
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, sort, onlineOnly, storeOnly]);

  useEffect(() => {
    if (query) fetchResults(query, 0);
  }, [query, fetchResults]);

  useEffect(() => {
    apiService.getCategoryTree().then((data) => setCategoryIndex(buildCategoryIndex(data.categories))).catch(() => {});
  }, []);

  // A new search starts with a clean set of facet filters.
  useEffect(() => {
    setTypeFilter(null);
    setDimFilters({});
  }, [query]);

  const facets = useMemo(
    () => applyFacets(results, { type: typeFilter, dims: dimFilters }, categoryIndex || new Map()),
    [results, typeFilter, dimFilters, categoryIndex]
  );

  const chooseType = (type) => {
    setTypeFilter((cur) => (cur === type ? null : type));
    setDimFilters({});
    setOffset(0);
  };

  const chooseDim = (label, value) => {
    setDimFilters((cur) => {
      const next = { ...cur };
      if (next[label] === value) delete next[label];
      else next[label] = value;
      return next;
    });
    setOffset(0);
  };

  const handleSortChange = (newSort) => {
    setSort(newSort);
    fetchResults(query, 0, newSort, onlineOnly);
  };

  const normalizeProduct = (item) => ({
    id: item.variation_id,
    name: item.name,
    description: item.description,
    description_html: item.description_html,
    price: item.price_money ? item.price_money.amount / 100 : 0,
    category: (item.categories || [])[0] || '',
    image: (item.image_urls || [])[0] || null,
    variation_id: item.variation_id,
    variation_name: item.variation_name,
    location_id: item.location_id,
    present_at_location_ids: item.present_at_location_ids || [],
    quantities_by_location: item.quantities_by_location || {},
    online_available: item.online_available === true,
    // Events share the catalog with physical products, so they turn up in
    // search results. Dropping this here is what let a tournament entry into
    // the cart as a shippable good.
    is_event: item.is_event === true,
  });

  const filteredTotal = facets.filtered.length;
  const displayedResults = facets.filtered.slice(offset, offset + limit);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container mx-auto px-4">
        {/* Search bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <SearchBox initialQuery={query} />
        </div>

        {/* Header row */}
        {query && !loading && (
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(f => !f)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-primary-100 text-primary-600 border-primary-300' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {filteredTotal > 0
                  ? <>Results for <span className="text-primary-600">"{query}"</span> <span className="text-gray-500 text-lg font-normal">({filteredTotal} items)</span></>
                  : <>No results for <span className="text-primary-600">"{query}"</span></>
                }
              </h1>
            </div>
            {total > 0 && (
              <select
                value={sort || ''}
                onChange={(e) => handleSortChange(e.target.value || null)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-primary-500"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value ?? ''} value={opt.value ?? ''}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <AnimatePresence>
            {showFilters && (
              <motion.aside
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="w-64 flex-shrink-0"
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  {/* Product type, built from the results */}
                  {facets.types.length > 1 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Product Type</h3>
                      <FacetList
                        options={facets.types}
                        selected={typeFilter}
                        onSelect={chooseType}
                      />
                    </div>
                  )}

                  {/* Drill-down for the chosen type (game / color / type / set) */}
                  {typeFilter && facets.dimensions.map((dim) => (
                    <div key={dim.label} className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{dim.label}</h3>
                      <FacetList
                        options={dim.options}
                        selected={dimFilters[dim.label]}
                        onSelect={(value) => chooseDim(dim.label, value)}
                        searchPlaceholder={dim.label === 'Set' ? 'Search sets...' : undefined}
                        expanded={Boolean(expandedDims[dim.label])}
                        onToggleExpanded={() => setExpandedDims((cur) => ({ ...cur, [dim.label]: !cur[dim.label] }))}
                      />
                    </div>
                  ))}

                  {/* Store Location */}
                  {locations.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Store Location</h3>
                      <div className="space-y-2">
                        {locations.map(loc => (
                          <label key={loc.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="store-location-search"
                              checked={selectedLocation?.id === loc.id}
                              onChange={() => {}}
                              onClick={() => setSelectedLocation(selectedLocation?.id === loc.id ? null : loc)}
                              className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 flex-shrink-0"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-800">{loc.name}</div>
                              <div className="text-xs text-gray-500">{loc.address?.address_line1}, {loc.address?.locality}</div>
                            </div>
                          </label>
                        ))}
                        {selectedLocation && (
                          <button
                            onClick={() => setSelectedLocation(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                          >
                            Show all locations
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Availability */}
                  <div className="border-t pt-5">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Availability</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={onlineOnly}
                          onChange={e => { setOnlineOnly(e.target.checked); if (e.target.checked) setStoreOnly(false); fetchResults(query, 0, sort, e.target.checked, false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Available Online</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={storeOnly}
                          onChange={e => { setStoreOnly(e.target.checked); if (e.target.checked) setOnlineOnly(false); fetchResults(query, 0, sort, false, e.target.checked); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">In Store Only</span>
                      </label>
                    </div>
                  </div>

                  {/* Clear Filters */}
                  <button
                    onClick={() => { setSelectedLocation(null); setOnlineOnly(false); setStoreOnly(false); setTypeFilter(null); setDimFilters({}); }}
                    className="w-full mt-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Clear All Filters
                  </button>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Results */}
          <div className="flex-1">
            {/* Loading */}
            {loading && (
              <div className="flex justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
              </div>
            )}

            {/* Error */}
            {error && <p className="text-center text-red-500 py-12">{error}</p>}

            {/* Empty state */}
            {!loading && !error && query && facets.filtered.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg mb-4">No products found matching "{query}".</p>
                <Link to="/shop" className="text-primary-600 hover:underline text-sm">Browse all products</Link>
              </div>
            )}

            {/* SKU match banner */}
            {!loading && skuMatch && displayedResults.length > 0 && (
              <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <span className="text-green-600 font-medium text-sm">Exact SKU match found for "{query}"</span>
              </div>
            )}

            {/* Results grid */}
            {!loading && displayedResults.length > 0 && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                >
                  {displayedResults.map((item, i) => (
                    <motion.div
                      key={item.variation_id || i}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <ProductCard
                        product={normalizeProduct(item)}
                        imageUrl={(item.image_urls || [])[0]}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-8">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    {offset + 1}–{Math.min(offset + limit, filteredTotal)} of {filteredTotal}
                  </span>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= filteredTotal}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
